import { LookupDictionary } from "@/Common/Models/LookupDictionary";

Array.prototype.mutate = function<T>(this: T[], mutation: (item: T) => void, where: (item: T) => boolean): T[] {
    return this.map(x => {
        if (where == null || where(x) == true)
            mutation(x);

        return x;
    });
};

Array.prototype.toLookupDictionary = function<T>(this: T[], getKey: (item: T) => string): LookupDictionary<T> {
    return new LookupDictionary<T>(getKey, this);
};

Array.prototype.contains = function<T>(this: T[], item: T): boolean {
    return this.some(p => p == item);
};

Array.prototype.toHash = function<TEntry, TValue>(this: TEntry[], getKey: (entry: TEntry) => string, getValue: (entry: TEntry) => TValue): Record<string, TValue> {
    return this.reduce(
        (obj, entry) => {
            const key = getKey(entry);
            const value = getValue ? getValue(entry) : entry;
            obj[key] = value;
            return obj;
        },
        {}
    );
};

Array.prototype.max = function(this: number[]): number {
    return Math.max.apply(null, this);
};

Array.prototype.min = function(this: number[]): number {
    return Math.min.apply(null, this);
};

Array.prototype.groupBy = function<T>(this: T[], getGroupKey: (item: T) => string | number): T[][] {
    const groups = {};

    for (const o of this) {
        const group = getGroupKey(o);
        groups[group] = groups[group] || [];
        groups[group].push(o);
    }

    return Object
        .keys(groups)
        .map(group => groups[group]);
};

Array.prototype.toLookup = function<T>(this: T[], getGroupKey: (item: T) => string | number): { [key: string | number]: T[] } {
    const groups: { [key: string | number]: T[] } = {};

    for (const o of this) {
        const group = getGroupKey(o);
        groups[group] = groups[group] || [];
        groups[group].push(o);
    }

    return groups;
};

Array.prototype.toDictionary = function<T>(this: T[], getKey: (item: T) => string | number): { [key: string | number]: T } {
    const groups: { [key: string | number]: any } = {};
    for (const obj of this)
        groups[getKey(obj)] = obj;

    return groups;
};

Array.prototype.firstOrDefault = function<T>(this: T[], condition: (item) => boolean = null, defaultValue: T = null) {
    const myArray: any[] = this;
    if (myArray == null) return defaultValue;

    if (condition == null) return myArray[0] || defaultValue;

    const len = myArray.length >>> 0;
    let k = 0;

    while (k < len) {
        const kValue = myArray[k];
        if (condition(kValue)) {
            return kValue;
        }
        k++;
    }

    return defaultValue;
};

Array.prototype.firstPreferredOrDefault = function <TValue>(this: TValue[], preference: (item: TValue) => boolean): TValue {
    if (!this || this.length == 0)
        return null;
    if (!preference)
        return this.firstOrDefault();

    return this.firstOrDefault(preference) || this.firstOrDefault();
};

Array.prototype.mergeWithPartial = function<T>(this: T[], partialArray: T[], equalityComparer: (value: T) => boolean): T[] {
    if (!this.length)
        return [...(partialArray ?? [])];

    const newElements = partialArray.filter(p => !this.some(e => equalityComparer(e) === equalityComparer(p)));
    const updatedArray = this.map(e => {
        const updatedElement = partialArray.firstOrDefault(p => equalityComparer(e) === equalityComparer(p));
        return (updatedElement) ? updatedElement : e;
    });

    return updatedArray.concat(newElements);
};

Array.prototype.filterSearch = function<T>(this: T[], searchText: string, getProperty: (item: T) => string): T[] {
    return this.filterSearchAdvanced(searchText, (BaseEvalFct, item) => BaseEvalFct(getProperty(item)));
};

/**
 * Filter an array so it only contains unique values.
 * @param this The array to filter.
 */
Array.prototype.distinct = function<T>(this: T[]): T[] {
    return this.distinctByProperty(p => p);
};

/**
 * Filter an array so it only contains unique values.
 * @param this The array to filter.
 * @param getKey Get a key which identifies an array element.
 */
Array.prototype.distinctByProperty = function<T>(this: T[], getKey: (value: T) => any): T[] {
    const found = {};
    return this.filter(value => {
        const key = getKey(value);
        if (!found[key]) {
            found[key] = true;
            return true;
        }
        return false;
    });
};

Array.prototype.toImmutable = function() {
    return Immutable.List(this);
};

/**
 * Sort an array based on a sort key.
 * @param getSortKey Get the sort key for an array value.
 * @param descending Whether to sort in descending order; else sort in ascending order.
 */
Array.prototype.sortByProperty = function <T>(this: T[], getSortKey: (value: T) => any, descending: boolean = false): T[] {
    return this.sort((cur, next) => {
        const left = getSortKey(cur);
        const right = getSortKey(next);
        if (left == right)
            return 0;

        const result = left > right ? 1 : -1;
        return descending
            ? result * -1
            : result;
    });
};

/**
 * Sort an array in descending order based on a sort key.
 * @param getSortKey Get the sort key for an array value.
 */
Array.prototype.sortByPropertyDesc = function<T>(this: T[], getSortKey: (value: T) => any): T[] {
    return this.sortByProperty(getSortKey, true);
};

Array.prototype.sortByProperties = function<T>(this: T[], ...comparers: Comparer<T>[]): T[] {
    return this.sort((cur, next) => {
        let order = 0;
        let i = 0;

        while (!order && comparers[i]) {
            order = comparers[i++](cur, next);
        }

        return order;
    });
};

Array.prototype.filterSearchAdvanced = function<T>(this: T[], searchText: string, evaluationFunction: (evaluate: (value: string) => boolean, item: T) => boolean): T[] {
    if (searchText == null && searchText == "")
        return this;
    else
        return this.filter(x => {
            const CleanText: string = cacheStore.Helper.RemoveDiacritics(searchText).toLowerCase();
            const splittedSearch = CleanText.split(" ");

            let isMatchingTerm1: (value: string) => boolean = () => false;
            let isMatchingTerm2: (value: string) => boolean = () => true;
            let isMatchingTerm3: (value: string) => boolean = () => true;

            if (splittedSearch != null && splittedSearch.length > 0)
                isMatchingTerm1 = (value: string) => {
                    if (value == null)
                        return false;

                    const ValueClean = cacheStore.Helper.RemoveDiacritics(value).toLowerCase();
                    const splittedvalue = ValueClean.split(" ");
                    return splittedvalue.some(word => word.indexOf(splittedSearch[0]) == 0);
                };

            if (splittedSearch != null && splittedSearch.length > 1)
                isMatchingTerm2 = (value: string) => {
                    if (value == null)
                        return false;

                    const ValueClean = cacheStore.Helper.RemoveDiacritics(value).toLowerCase();
                    return ValueClean.indexOf(splittedSearch[1]) >= 0;
                };

            if (splittedSearch != null && splittedSearch.length > 2)
                isMatchingTerm3 = (value: string) => {
                    if (value == null)
                        return false;

                    const ValueClean = cacheStore.Helper.RemoveDiacritics(value).toLowerCase();
                    return ValueClean.indexOf(splittedSearch[2]) >= 0;
                };

            const condition1Respected = evaluationFunction(isMatchingTerm1, x);
            const condition2Respected = evaluationFunction(isMatchingTerm2, x);
            const condition3Respected = evaluationFunction(isMatchingTerm3, x);

            return condition1Respected && condition2Respected && condition3Respected;
        });
};

Array.prototype.injectArray = function<T>(this: T[], idx: number, arr: T[]): T[] {
    return this.slice(0, idx).concat(arr).concat(this.slice(idx));
};

Array.prototype.move = function<T>(old_index: number, new_index: number): T[] {
    if (new_index >= this.length) {
        let k = new_index - this.length;
        while ((k--) + 1) {
            this.push(undefined);
        }
    }
    this.splice(new_index, 0, this.splice(old_index, 1)[0]);
    return this; // for testing purposes
};

Array.prototype.indexOfObject = function<T>(this: T[], property: string, value: any): number {
    for (let i = 0, len = this.length; i < len; i++) {
        if (this[i][property] === value)
            return i;
    }
    return -1;
};

Array.prototype.any = function<T>(this: T[], predicate: (item: T) => boolean = null): boolean {
    if (predicate == null) {
        return !!(this && this.length);
    }

    for (const item of this) {
        if (predicate(item))
            return true;
    }

    return false;
};

Array.prototype.all = function<T>(this: T[], predicate: (item: T) => boolean = null): boolean {
    if (predicate == null) {
        throw "predicate can not be null";
    }

    if (!this || !this.length)
        return true;

    for (const item of this) {
        if (!predicate(item))
            return false;
    }

    return true;
};

Array.prototype.intersect = function<T>(this: T[], other: T[], comparer: (a: T, b: T) => boolean): T[] {
    if (!this || !this.length || !other || !other.length)
        return [];

    const compareFunc = comparer
        ? comparer
        : (a, b) => a === b;

    const outArray = [];
    for (const a of this) {
        if (other.any(b => compareFunc(a, b))) outArray.push(a);
    }

    return outArray;
};

/**
 * Flatten multiple values per item into one list. This is a polyfill for Array.flatMap until we update TypeScript.
 * @param array The array to flatten.
 * @param getInner Get the values for an item.
 */
Array.prototype.flatten = function<T, TInner>(this: T[], getInner: (value: T, index: number, array: T[]) => TInner[]): TInner[] {
    return Array.prototype.concat(...this.map(getInner)); // from https://gist.github.com/karol-majewski/e1a53b9abd39f3b7c3f4bf150546168a
};

Array.prototype.isSorted = function<T>(this: T[], comparer: (a: T, b: T) => boolean, ignoreNullOrUndefined: boolean = true): boolean {
    const myArray: T[] = ignoreNullOrUndefined
        ? this.filter(x => x !== null && x !== undefined)
        : this;

    for (let i = 0; i < (myArray.length - 1); i++) {
        if (!comparer(myArray[i], myArray[i + 1]))
            return false;
    }

    return true;
};

Array.prototype.upsert = function <T>(this: T[], newArray: T[] | T, isMatch: (a: T, b: T) => boolean): T[] {
    const arr = [...this];
    const toInsert: T[] = [];

    if (!Array.isArray(newArray))
        newArray = [newArray];

    for (const newElem of newArray) {
        let oldElem = arr.firstOrDefault(a => isMatch(a, newElem));
        if (oldElem)
            oldElem = newElem;
        else
            toInsert.push(newElem);
    }

    return [...arr, ...toInsert];
};

Array.prototype.toggleElement = function <T>(this: T[], element: T): void {
    const newArray: T[] = this;
    const indexOfElement = newArray.indexOf(element);

    indexOfElement > -1
        ? newArray.splice(indexOfElement, 1)
        : newArray.push(element);
};
